import { useState, useEffect } from 'react';

interface MobileDetectResult {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  userAgent: string;
}

/**
 * Hook to detect if the user is on a mobile device
 * Uses user agent detection and screen size as fallback
 */
export function useMobileDetect(): MobileDetectResult {
  const [result, setResult] = useState<MobileDetectResult>(() => detectDevice());

  useEffect(() => {
    // Re-check on resize (for responsive testing/dev tools)
    const handleResize = () => {
      setResult(detectDevice());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return result;
}

/**
 * Detect device type based on user agent and screen size
 */
function detectDevice(): MobileDetectResult {
  if (typeof window === 'undefined') {
    return { isMobile: false, isTablet: false, isDesktop: true, userAgent: '' };
  }

  const userAgent = navigator.userAgent || navigator.vendor || '';

  // Mobile device patterns
  const mobilePatterns = [
    /Android/i,
    /webOS/i,
    /iPhone/i,
    /iPod/i,
    /BlackBerry/i,
    /IEMobile/i,
    /Opera Mini/i,
    /Windows Phone/i,
    /Mobile/i,
  ];

  // Tablet patterns
  const tabletPatterns = [
    /iPad/i,
    /Android(?!.*Mobile)/i, // Android without "Mobile" is usually tablet
    /Tablet/i,
  ];

  const isMobileUA = mobilePatterns.some(pattern => pattern.test(userAgent));
  const isTabletUA = tabletPatterns.some(pattern => pattern.test(userAgent));

  // Also check screen width as a fallback
  const screenWidth = window.innerWidth;
  const isMobileScreen = screenWidth < 768;
  const isTabletScreen = screenWidth >= 768 && screenWidth < 1024;

  // Combine UA detection with screen size
  // Consider mobile if: mobile UA OR (small screen AND touch device)
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  const isMobile = isMobileUA || (isMobileScreen && hasTouch && !isTabletUA);
  const isTablet = isTabletUA || (isTabletScreen && hasTouch && !isMobileUA);
  const isDesktop = !isMobile && !isTablet;

  return {
    isMobile,
    isTablet,
    isDesktop,
    userAgent,
  };
}

/**
 * Check if device is mobile (can be used outside React)
 */
export function isMobileDevice(): boolean {
  return detectDevice().isMobile;
}

/**
 * Check if device is mobile or tablet
 */
export function isMobileOrTablet(): boolean {
  const { isMobile, isTablet } = detectDevice();
  return isMobile || isTablet;
}
